import { useEffect, useRef } from "react";

import { formatError } from "@/lib/errors";
import { useToast } from "@/components/ui/toast";

const DEFAULT_FALLBACK = "Something went wrong.";

interface ErrorMessageProps {
  /** Anything thrown — `ApiError`, `Error`, or string. Falsy shows nothing. */
  error: unknown;
  /** Contextual headline, e.g. "Failed to load employees." */
  fallback?: string;
  /** Accepted for call-site compatibility; toasts ignore layout classes. */
  className?: string;
}

/** Single source of truth for how the UI surfaces failed requests: pushes
 *  an error toast (headline + readable server message + support reference)
 *  whenever it receives a new error. Renders nothing inline. The toast
 *  stack dedupes, so several widgets failing the same way — e.g. every
 *  query on a page hitting the same 403 — surface a single toast. */
export function ErrorMessage({
  error,
  fallback = DEFAULT_FALLBACK,
}: ErrorMessageProps) {
  const toast = useToast();
  const lastErrorRef = useRef<unknown>(null);

  useEffect(() => {
    if (!error || lastErrorRef.current === error) return;
    lastErrorRef.current = error;

    const { message, requestId } = formatError(error);
    // With the generic fallback, promote the server message to the headline
    // instead of burying it under "Something went wrong."
    const title =
      fallback === DEFAULT_FALLBACK && message ? message : fallback;
    const parts: string[] = [];
    if (message && message !== title) parts.push(message);
    if (requestId) parts.push(`Ref: ${requestId}`);
    toast.error(title, parts.length ? parts.join(" · ") : undefined);
  }, [error, fallback, toast]);

  return null;
}
